// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseAvatarGroup,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseTable,
  BaseTableUser,
  BaseTextEditor,
  BaseToolbar,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { FormReply } from '~/components/module/review';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { mockDataReviewRating, mockDataReviewSource } from '~/utils/mockData/estimate';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getReviewList } from '~/thunks/review/reviewThunk';
import { IPaginationResponse } from '~/utils/interface/base';
import useDebounce from '~/utils/hooks/useDebounce';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import {
  IBaseMoreAction,
  IFilterValueChange,
  IHeaderCellType,
  IQueryParamBase,
} from '~/utils/interface/common';
import { IReview } from '~/utils/interface/review';
import { formatDate, formatTitleCase } from '~/utils/helper/common';
import {
  DateFormatEnum,
  ModeTextEditorEnum,
  ReviewSourceEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
  ThemeTextEditorEnum,
} from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { StorageEnum } from '~/utils/enum/base';
import { ModeActionType } from '~/utils/type/common';
import { selectIsRefreshReviewList } from '~/thunks/review/reviewSlice';
// Styles, images, icons
import styles from './SubReview.module.scss';
import { icons, images } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onViewJob: (jobId: string) => void,
  onReply: (record: IReview) => void,
  onShowReview: (record: IReview) => void
): IHeaderCellType<IReview>[] => {
  return [
    {
      id: 'customerName',
      label: t('table_head_title_customer_name'),
      render(record) {
        return record.customerName || EMPTY_STRING;
      },
      width: 200,
    },
    {
      id: 'createdDate',
      label: t('table_head_title_created_date'),
      render(record) {
        return record.createdAt ? formatDate(record?.createdAt, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
      width: 100,
    },
    {
      id: 'employee',
      label: t('table_head_title_employee'),
      render(record) {
        const { employees } = record;
        return employees && employees.length === DEFAULT_NUMBER_ONE ? (
          <BaseTableUser
            user={{
              avatar: employees[DEFAULT_NUMBER_ZERO]?.avatarImage,
              firstName: employees[DEFAULT_NUMBER_ZERO]?.firstName,
              lastName: employees[DEFAULT_NUMBER_ZERO]?.lastName,
            }}
          />
        ) : (
          <BaseAvatarGroup avatars={employees || []} borderColor='#FFF' max={5} />
        );
      },
      width: 200,
    },
    {
      id: 'rating',
      label: t('table_head_title_rating'),
      render(record) {
        return record.rating || EMPTY_STRING;
      },
      width: 50,
    },
    {
      id: 'source',
      label: t('table_head_title_source'),
      render(record) {
        return record.source === ReviewSourceEnum.INTERNAL ? (
          <Box component={'div'} className={cx('sourceWrap')}>
            <img
              src={images.imgLogo}
              alt={t('common_alt_img', {
                ns: 'common/base',
              })}
              className={cx('logo')}
            />

            <Typography className={cx('text')}>
              {t('system_title', {
                ns: 'common/base',
              })}
            </Typography>
          </Box>
        ) : (
          <Box component={'div'} className={cx('sourceWrap')}>
            <img
              src={icons.integrationIconGoogleLogo}
              alt={t('common_alt_img', {
                ns: 'common/base',
              })}
              className={cx('icons')}
            />

            <Typography className={cx('text')}>
              {t('google_title', {
                ns: 'common/base',
              })}
            </Typography>
          </Box>
        );
      },
      width: 200,
    },
    {
      id: 'content',
      label: t('table_head_title_content'),
      render(record) {
        return record.content ? (
          <BaseTextEditor
            mode={ModeTextEditorEnum.VIEW}
            width={'100%'}
            theme={ThemeTextEditorEnum.BUBBLE}
            value={record.content}
          />
        ) : (
          EMPTY_STRING
        );
      },
    },
    {
      id: 'replyStatus',
      label: t('table_head_title_reply_status'),
      width: 100,
      render(record) {
        return (
          <Box
            component={'div'}
            className={cx('reply', { responded: record?.reply.length > DEFAULT_NUMBER_ZERO })}
          ></Box>
        );
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      render(record) {
        const actions: IBaseMoreAction[] = [];
        if (record.source === ReviewSourceEnum.INTERNAL) {
          actions.push({
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_view_job'),
            onClick: () => onViewJob(record.jobId),
          });
        }

        if (!record.reply || record.reply.length === DEFAULT_NUMBER_ZERO) {
          actions.push({
            icon: (
              <img
                src={icons.commonIconUndo}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_reply'),
            onClick: () => onReply(record),
          });
        }

        if (record.reply && record.reply.length > DEFAULT_NUMBER_ZERO) {
          actions.push({
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_show_reply'),
            onClick: () => onShowReview(record),
          });
        }
        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const SubReview = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/review']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshReviewList = useSelector(selectIsRefreshReviewList);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [filterParams, setFilterParams] = useState<IQueryParamBase>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [reviewList, setReviewList] = useState<IReview[]>([]);
  const [totalItems, setTotalItems] = useState<string>(EMPTY_STRING);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReply, setIsReply] = useState<boolean>(false);
  const [modeActionReply, setModeActionReply] = useState<ModeActionType>('create');
  const [reviewSelected, setReviewSelected] = useState<IReview>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const hasInitialized = useRef(false);

  const handleGetReview = useCallback(
    (params: IQueryParamBase) => {
      setIsLoading(true);
      dispatch(getReviewList(params))
        .unwrap()
        .then((res) => {
          const { responses, pagination, totalItems } = res?.data;

          if (!responses) return;

          setReviewList(responses);
          setPagination(pagination);
          setTotalItems(totalItems.toString());
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!isRefreshReviewList) return;
    handleGetReview({ ...paramObject });
  }, [isRefreshReviewList, paramObject, handleGetReview]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields
        ?.map((item) => {
          if (item === SearchByFilterEnum.ROLE) {
            return formatTitleCase(SearchByFilterEnum.POSITION);
          }

          return formatTitleCase(item);
        })
        ?.join(', ');

      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    handleGetReview({ ...paramObject });
  }, [paramObject, handleGetReview]);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    setParamObject((prev) => ({
      ...prev,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      ...(filterParams ? { ...filterParams } : {}),
    }));

    setSearchParams({
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(limitPage),
      ...(searchKey ? { textSearch: debounceSearchKey } : {}),
    });
  }, [debounceSearchKey, limitPage, filterParams, searchKey, setSearchParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleNavigateJob = (jobId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${jobId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${jobId}`);
        break;
    }
  };

  const handleReply = (record: IReview) => {
    setIsReply(true);
    setReviewSelected(record);
    setModeActionReply('create');
  };

  const handleCloseReply = () => {
    setIsReply(false);
    setReviewSelected(undefined);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter?: IQueryParamBase,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...paramObject,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }

    setFilterParams({ ...filterParams, fields: newFields?.fields });
  };

  const handleApplyFilter = (values?: IQueryParamBase) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.source || values.rating) {
      setParamObject({
        ...paramObject,
        ...(values?.source ? { source: values?.source } : {}),
        ...(values?.rating ? { rating: values.rating } : {}),
      });
    }
  };

  const handleShowReply = (record: IReview) => {
    setIsReply(true);
    setReviewSelected(record);
    setModeActionReply('view');
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_review', { ns: 'common/sidebar' })}>
    <Box component={'div'} className={cx('container')} id='subReviewPage'>
      <BaseToolbar title={t('tile_quantity_review_page', { quantity: totalItems })}>
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.CONTENT}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.CONTENT
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters value={filterParams} defaultValue={FILTER_DEFAULT_VALUES} onApply={handleApplyFilter}>
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>
              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.CUSTOMER}
                      checked={value?.fields?.includes(SearchByFilterEnum.CUSTOMER) || false}
                      onChange={(event) => handleFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_customer')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.EMPLOYEE}
                      checked={value?.fields?.includes(SearchByFilterEnum.EMPLOYEE) || false}
                      onChange={(event) => handleFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_employee')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_rate')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'135px'}
                  options={mockDataReviewRating}
                  value={value?.rating}
                  onChange={(optionSelected) => {
                    onChange &&
                      onChange({
                        name: 'rating',
                        value: optionSelected?.value.toString(),
                      });
                  }}
                />
              </Box>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_source')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'135px'}
                  options={mockDataReviewSource}
                  value={value?.source}
                  onChange={(optionSelected) => {
                    onChange &&
                      onChange({
                        name: 'source',
                        value: optionSelected?.value.toString(),
                      });
                  }}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleNavigateJob, handleReply, handleShowReply)}
          dataSource={reviewList ?? []}
          onPageChange={handlePageChange}
          // onRowClick={handleNavigateJob}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isReply && <FormReply onClose={handleCloseReply} mode={modeActionReply} data={reviewSelected} />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default SubReview;
